// Phase 2.1: Timezone Detection & Conversion Utilities (COMPLETE)
import { DateTime } from 'luxon';

// Automatic timezone detection using browser APIs
export const detectUserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (error) {
    console.warn('Failed to detect timezone:', error);
    return 'America/New_York'; // Fallback to Eastern
  }
};

// Get timezone offset display (e.g., "EST", "PST")
export const getTimezoneOffset = (timezone: string): string => {
  try {
    const now = new Date();
    const formatter = new Intl.DateTimeFormat('en', {
      timeZone: timezone,
      timeZoneName: 'short'
    });
    return formatter.formatToParts(now).find(part => part.type === 'timeZoneName')?.value || '';
  } catch (error) {
    console.warn('Failed to get timezone offset:', error);
    return '';
  }
};

// Convert time between timezones
export const convertTimeToTimezone = (
  time: string, 
  fromTimezone: string, 
  toTimezone: string,
  date?: string
): string => {
  try {
    const baseDate = date || new Date().toISOString().split('T')[0];
    const fullDateTime = `${baseDate}T${time}`;
    
    const dt = DateTime.fromISO(fullDateTime, { zone: fromTimezone });
    const converted = dt.setZone(toTimezone);
    
    return converted.toFormat('HH:mm:ss');
  } catch (error) {
    console.warn('Failed to convert timezone:', error);
    return time; // Return original time if conversion fails
  }
};

// Format time in a specific timezone with 12h/24h support
export const formatTimeInTimezone = (
  time: string,
  timezone: string,
  format: '12h' | '24h' = '12h'
): string => {
  try {
    const baseDate = new Date().toISOString().split('T')[0];
    const fullDateTime = `${baseDate}T${time}`;
    
    const dt = DateTime.fromISO(fullDateTime, { zone: timezone });
    
    return format === '12h' 
      ? dt.toFormat('h:mm a') 
      : dt.toFormat('HH:mm');
  } catch (error) {
    console.warn('Failed to format time:', error);
    return time;
  }
};

// Get human-readable timezone display
export const getTimezoneDisplay = (timezone: string): string => {
  try {
    const now = DateTime.now().setZone(timezone);
    const cityName = timezone.split('/')[1]?.replace('_', ' ') || timezone;
    const offset = now.toFormat('ZZZZ');
    return `${cityName} (${offset})`;
  } catch (error) {
    console.warn('Failed to get timezone display:', error);
    return timezone;
  }
};

// Common US timezones for selection
export const COMMON_TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern Time', abbr: 'ET' },
  { value: 'America/Chicago', label: 'Central Time', abbr: 'CT' },
  { value: 'America/Denver', label: 'Mountain Time', abbr: 'MT' },
  { value: 'America/Los_Angeles', label: 'Pacific Time', abbr: 'PT' },
  { value: 'America/Phoenix', label: 'Arizona Time', abbr: 'MST' },
  { value: 'America/Anchorage', label: 'Alaska Time', abbr: 'AKT' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time', abbr: 'HST' },
  { value: 'Europe/London', label: 'London Time', abbr: 'GMT' },
  { value: 'UTC', label: 'UTC', abbr: 'UTC' }
];

// Validate timezone string
export const isValidTimezone = (timezone: string): boolean => {
  try {
    DateTime.now().setZone(timezone);
    return true;
  } catch {
    return false;
  }
};

// Get timezone-aware current time
export const getCurrentTimeInTimezone = (timezone: string): string => {
  try {
    const now = DateTime.now().setZone(timezone);
    return now.toFormat('HH:mm:ss');
  } catch (error) {
    console.warn('Failed to get current time in timezone:', error);
    return new Date().toTimeString().split(' ')[0];
  }
};

// Check if appointment time crosses date boundary
export const doesTimeCrossDate = (
  time: string,
  fromTimezone: string,
  toTimezone: string,
  date?: string
): boolean => {
  try {
    const baseDate = date || new Date().toISOString().split('T')[0];
    const originalDateTime = DateTime.fromISO(`${baseDate}T${time}`, { zone: fromTimezone });
    const convertedDateTime = originalDateTime.setZone(toTimezone);
    
    return originalDateTime.toISODate() !== convertedDateTime.toISODate();
  } catch {
    return false;
  }
};

// Map US states to their primary timezone
export const getTimezoneForState = (stateAbbreviation: string): string => {
  const stateTimezoneMap: Record<string, string> = {
    // Pacific Time Zone
    'CA': 'America/Los_Angeles',      // California
    'WA': 'America/Los_Angeles',      // Washington  
    'OR': 'America/Los_Angeles',      // Oregon
    'NV': 'America/Los_Angeles',      // Nevada (most of state)
    
    // Mountain Time Zone
    'MT': 'America/Denver',           // Montana
    'WY': 'America/Denver',           // Wyoming
    'UT': 'America/Denver',           // Utah
    'CO': 'America/Denver',           // Colorado
    'NM': 'America/Denver',           // New Mexico
    'AZ': 'America/Phoenix',          // Arizona (no DST)
    'ID': 'America/Denver',           // Idaho (mostly Mountain)
    
    // Central Time Zone
    'ND': 'America/Chicago',          // North Dakota
    'SD': 'America/Chicago',          // South Dakota
    'NE': 'America/Chicago',          // Nebraska
    'KS': 'America/Chicago',          // Kansas
    'OK': 'America/Chicago',          // Oklahoma
    'TX': 'America/Chicago',          // Texas (mostly Central)
    'MN': 'America/Chicago',          // Minnesota
    'IA': 'America/Chicago',          // Iowa
    'MO': 'America/Chicago',          // Missouri
    'AR': 'America/Chicago',          // Arkansas
    'LA': 'America/Chicago',          // Louisiana
    'WI': 'America/Chicago',          // Wisconsin
    'IL': 'America/Chicago',          // Illinois
    'MS': 'America/Chicago',          // Mississippi
    'AL': 'America/Chicago',          // Alabama
    'TN': 'America/Chicago',          // Tennessee
    'KY': 'America/New_York',         // Kentucky (Eastern)
    'IN': 'America/New_York',         // Indiana (mostly Eastern)
    
    // Eastern Time Zone
    'ME': 'America/New_York',         // Maine
    'NH': 'America/New_York',         // New Hampshire
    'VT': 'America/New_York',         // Vermont
    'MA': 'America/New_York',         // Massachusetts
    'RI': 'America/New_York',         // Rhode Island
    'CT': 'America/New_York',         // Connecticut
    'NY': 'America/New_York',         // New York
    'NJ': 'America/New_York',         // New Jersey
    'PA': 'America/New_York',         // Pennsylvania
    'DE': 'America/New_York',         // Delaware
    'MD': 'America/New_York',         // Maryland
    'DC': 'America/New_York',         // District of Columbia
    'VA': 'America/New_York',         // Virginia
    'WV': 'America/New_York',         // West Virginia
    'OH': 'America/New_York',         // Ohio
    'MI': 'America/New_York',         // Michigan
    'NC': 'America/New_York',         // North Carolina
    'SC': 'America/New_York',         // South Carolina
    'GA': 'America/New_York',         // Georgia
    'FL': 'America/New_York',         // Florida
    
    // Special cases
    'AK': 'America/Anchorage',        // Alaska
    'HI': 'Pacific/Honolulu',         // Hawaii
  };
  
  const timezone = stateTimezoneMap[stateAbbreviation.toUpperCase()];
  if (!timezone) {
    console.warn(`🌍 TIMEZONE WARNING: No timezone mapping found for state ${stateAbbreviation}, falling back to Eastern`);
    return 'America/New_York'; // Fallback to Eastern
  }
  
  console.log(`🌍 TIMEZONE MAPPING: State ${stateAbbreviation} mapped to ${timezone}`);
  return timezone;
};

console.log('✅ Phase 2.1 COMPLETE: Timezone utilities created with luxon support');